/**
 * DriveBrowser Component
 * Interactive file browser for Google Drive shared drives, folders, and files
 */

import { useState, useEffect, useCallback } from "react";

// Types matching the backend API response
interface BrowseItem {
  id: string;
  name: string;
  type: "drive" | "folder" | "file";
  mimeType: string;
  modifiedTime?: string;
  selectable: boolean;
}

interface BrowseResponse {
  current: {
    id: string;
    name: string;
    type: "root" | "drive" | "folder";
  };
  parent?: {
    id: string;
    name: string;
    type: "root" | "drive" | "folder";
  };
  items: BrowseItem[];
}

export interface DriveSelection {
  type: "drive" | "folder" | "file";
  id: string;
  name: string;
  driveId: string;
  path: string[];
}

interface DriveBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selection: DriveSelection) => void;
}

// Map MIME types to human-readable names
const MIME_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.google-apps.document": "Google Doc",
  "application/vnd.google-apps.spreadsheet": "Google Sheets",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
};

// Get icon for item type
function getItemIcon(item: BrowseItem): string {
  if (item.type === "drive") return "🗄️";
  if (item.type === "folder") return "📁";

  // File icons based on MIME type
  if (item.mimeType === "application/pdf") return "📄";
  if (item.mimeType === "application/vnd.google-apps.document") return "📝";
  if (item.mimeType === "application/vnd.google-apps.spreadsheet") return "📊";
  if (item.mimeType.includes("word")) return "📃";
  return "📄";
}

export function DriveBrowser({ isOpen, onClose, onSelect }: DriveBrowserProps) {
  const [items, setItems] = useState<BrowseItem[]>([]);
  const [current, setCurrent] = useState<BrowseResponse["current"] | null>(null);
  const [parent, setParent] = useState<BrowseResponse["parent"]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the current drive ID and path for breadcrumbs
  const [currentDriveId, setCurrentDriveId] = useState<string | null>(null);
  const [path, setPath] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch items from the API
  const fetchItems = useCallback(async (parentId?: string, driveId?: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (parentId) params.set("parentId", parentId);
      if (driveId) params.set("driveId", driveId);

      const response = await fetch(`/api/admin/google/drive/browse?${params}`, {
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? "Fout bij laden van bestanden");
      }

      const data: BrowseResponse = await response.json();
      setItems(data.items);
      setCurrent(data.current);
      setParent(data.parent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Onbekende fout");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load root (shared drives) when opened
  useEffect(() => {
    if (isOpen) {
      setPath([]);
      setCurrentDriveId(null);
      void fetchItems();
    }
  }, [isOpen, fetchItems]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Backspace" && parent) {
        handleNavigateUp();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, parent, onClose]);

  // Navigate into a folder/drive
  const handleNavigate = (item: BrowseItem) => {
    if (item.type === "drive") {
      setCurrentDriveId(item.id);
      setPath([{ id: item.id, name: item.name }]);
      void fetchItems(item.id, item.id);
    } else if (item.type === "folder") {
      setPath((prev) => [...prev, { id: item.id, name: item.name }]);
      void fetchItems(item.id, currentDriveId ?? undefined);
    }
  };

  // Navigate up to parent
  const handleNavigateUp = () => {
    if (!parent) return;

    if (parent.type === "root") {
      setPath([]);
      setCurrentDriveId(null);
      void fetchItems();
    } else {
      setPath((prev) => prev.slice(0, -1));
      void fetchItems(parent.id, currentDriveId ?? undefined);
    }
  };

  // Navigate to specific breadcrumb
  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Root
      setPath([]);
      setCurrentDriveId(null);
      void fetchItems();
    } else {
      const targetPath = path.slice(0, index + 1);
      const targetItem = targetPath[targetPath.length - 1];
      if (!targetItem) return;

      setPath(targetPath);

      if (index === 0) {
        // Clicked on drive
        void fetchItems(targetItem.id, targetItem.id);
      } else {
        // Clicked on folder
        void fetchItems(targetItem.id, currentDriveId ?? undefined);
      }
    }
  };

  // Select an item (drive, folder, or file)
  const handleSelect = (item: BrowseItem) => {
    if (!item.selectable) return;

    const selection: DriveSelection = {
      type: item.type,
      id: item.id,
      name: item.name,
      driveId: item.type === "drive" ? item.id : (currentDriveId ?? item.id),
      path: path.map((p) => p.name),
    };

    onSelect(selection);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-scrim flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-container-lowest rounded-lg shadow-3 w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-on-surface">📁 Selecteer uit Google Drive</h2>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface text-2xl leading-none"
            aria-label="Sluiten"
          >
            ×
          </button>
        </div>

        {/* Breadcrumbs */}
        <div className="px-4 py-2 bg-surface-dim border-b border-outline-variant flex items-center gap-1 text-sm overflow-x-auto">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className="text-primary hover:text-primary/80 font-medium flex-shrink-0"
          >
            🏠 Gedeelde drives
          </button>
          {path.map((item, index) => (
            <span key={item.id} className="flex items-center gap-1 flex-shrink-0">
              <span className="text-on-surface-variant/40">/</span>
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className={`hover:text-primary ${
                  index === path.length - 1 ? "text-on-surface font-medium" : "text-primary"
                }`}
              >
                {item.name}
              </button>
            </span>
          ))}
        </div>

        {/* Select current folder/drive option */}
        {current && current.type !== "root" && (
          <div className="px-4 py-3 bg-primary-container/30 border-b border-outline-variant flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{current.type === "drive" ? "🗄️" : "📁"}</span>
              <div>
                <span className="font-medium text-on-surface">
                  {current.type === "drive" ? "Deze drive selecteren" : "Deze map selecteren"}
                </span>
                <span className="text-on-surface-variant text-sm ml-2">({current.name})</span>
              </div>
            </div>
            <button
              onClick={() =>
                handleSelect({
                  id: current.id,
                  name: current.name,
                  type: current.type === "drive" ? "drive" : "folder",
                  mimeType: "application/vnd.google-apps.folder",
                  selectable: true,
                })
              }
              className="bg-primary hover:bg-primary/90 text-on-primary px-4 py-1.5 rounded-md text-sm font-semibold transition-colors"
            >
              Selecteer
            </button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              <span className="ml-3 text-on-surface-variant">Laden...</span>
            </div>
          )}

          {error && (
            <div className="bg-error-container border border-error/30 text-on-error-container px-4 py-3 rounded-md">
              <p className="font-semibold">Fout</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="text-center py-12 text-on-surface-variant">
              <p className="text-4xl mb-2">📭</p>
              <p>Geen bestanden gevonden</p>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="space-y-1">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-md transition-colors ${
                    item.selectable
                      ? "hover:bg-surface-container cursor-pointer"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  {/* Icon and name */}
                  <div
                    className="flex-1 flex items-center gap-3 min-w-0"
                    onClick={() => {
                      if (item.type === "drive" || item.type === "folder") {
                        handleNavigate(item);
                      }
                    }}
                  >
                    <span className="text-2xl flex-shrink-0">{getItemIcon(item)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-on-surface truncate">{item.name}</p>
                      {item.type === "file" && (
                        <p className="text-xs text-on-surface-variant">
                          {MIME_TYPE_LABELS[item.mimeType] ?? item.mimeType}
                          {item.modifiedTime && (
                            <span className="ml-2">
                              • {new Date(item.modifiedTime).toLocaleDateString("nl-NL")}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(item.type === "drive" || item.type === "folder") && (
                      <button
                        onClick={() => handleNavigate(item)}
                        className="text-on-surface-variant hover:text-primary px-2 py-1 text-sm"
                        title="Openen"
                      >
                        Open →
                      </button>
                    )}
                    {item.selectable && (
                      <button
                        onClick={() => handleSelect(item)}
                        className="bg-primary hover:bg-primary/90 text-on-primary px-3 py-1 rounded-sm text-sm font-medium transition-colors"
                      >
                        Selecteer
                      </button>
                    )}
                    {!item.selectable && item.type === "file" && (
                      <span className="text-xs text-on-surface-variant/60 italic">
                        Niet ondersteund
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-outline-variant bg-surface-dim flex justify-between items-center">
          <p className="text-xs text-on-surface-variant">
            Ondersteunde bestanden: PDF, Google Docs, Google Sheets, Word
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-container hover:bg-surface-container-high text-on-surface rounded-md font-semibold transition-colors"
          >
            Annuleer
          </button>
        </div>
      </div>
    </div>
  );
}
